import { useMemo } from 'react';

import type { LWApiPublicSupportRequestMigrationStatusPicklistOptionsResultRaw } from '@liquidweb-dev/club-sauce/public/support/request/index.raw';
import Drawer, { type DrawerProps } from '@liquidweb-dev/undercurrent/Drawer';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';

export const MIGRATION_STATUS_INFO: Record<string, string> = {
	'Not Started': 'Request received, awaiting assignment to team.',
	Assessment: 'Requirements are being reviewed.',
	'Initial Sync':
		'We are copying data from your source environment to the destination environment.',
	'Testing Phase':
		'Your migrated environment is ready for you or your team to review for issues.',
	'Final Scheduled': 'Your final sync or go-live activity has been scheduled.',
	'Post-Go Live': 'We are completing the final validation.',
	'Migration Completed':
		'Your migration is complete, the destination environment is now live.',
	'Migration Cancelled':
		'This migration has been cancelled and will need to be resubmitted.',
};

export interface DrawerMigrationStatusProps
	extends Omit<DrawerProps, 'onClose'> {
	migrationStatuses?: LWApiPublicSupportRequestMigrationStatusPicklistOptionsResultRaw;
	onClose: () => void;
}

export interface DrawerWrapperProps extends Omit<DrawerProps, 'onClose'> {
	onClose?: () => void;
}

const DrawerWrapper = ({
	children,
	onClose,
	...drawerProps
}: DrawerWrapperProps) => {
	return (
		<Drawer anchor="right" onClose={onClose} {...drawerProps}>
			<DrawerHeader
				onClose={onClose}
				description={
					<Typography sx={{ color: ({ vars }) => vars.palette.uc.text.medium }}>
						What each one means for you
					</Typography>
				}
			>
				<Typography
					variant="title-xs"
					sx={{
						fontWeight: 500,
					}}
				>
					Migration Statuses
				</Typography>
			</DrawerHeader>
			{children}
		</Drawer>
	);
};

const DrawerMigrationStatus = ({
	migrationStatuses,
	onClose,
	...drawerProps
}: DrawerMigrationStatusProps) => {
	const migrationStatusDescriptions = useMemo(() => {
		if (!migrationStatuses) return [];
		return migrationStatuses?.statuses.map((status) => ({
			...status,
			description: MIGRATION_STATUS_INFO[status.key],
		}));
	}, [migrationStatuses]);

	return (
		<DrawerWrapper onClose={onClose} {...drawerProps}>
			<DrawerContent>
				<Stack direction="column" sx={{ gap: 300 }}>
					{migrationStatusDescriptions.map((item) => (
						<Typography key={item.key} variant="body-md">
							<Typography
								component="span"
								sx={({ palette }) => ({
									color: palette.uc.text.main,
									fontWeight: 700,
								})}
							>
								{item.value}
							</Typography>
							{' — '}
							<Typography component="span">{item.description}</Typography>
						</Typography>
					))}
				</Stack>
			</DrawerContent>
		</DrawerWrapper>
	);
};

export default DrawerMigrationStatus;
